import { formatDealerDateOnly } from '@/utils/vehicleFieldValueNormalization'
import {
  formatBooleanDisplayValue,
  isVehicleHistoryBooleanField,
} from '@/utils/vehicleHistoryBooleanFields'
import { isModelEField } from '@/utils/editVehicleBooleanFields'

const OVERRIDE_FIELD_RELEASE_LABELS: Record<string, string> = {
  condition: 'Condition',
  certified_by_dealer: 'Certification',
  certified_by_manufacturer: 'Certification',
  mileage: 'Mileage',
  price: 'Price',
}

export type OverrideFieldDisplayOptions = {
  dealerTimeZone?: string
}

/** Human-readable field name for release confirmation copy. */
export function formatOverrideFieldReleaseLabel(field: string): string | null {
  const normalized = String(field || '').trim()
  if (!normalized) return null
  return OVERRIDE_FIELD_RELEASE_LABELS[normalized] ?? null
}

/** Formats feed-backed override field values for compact diff display. */
export function formatOverrideFieldDisplayValue(
  field: string,
  value: unknown,
  options?: OverrideFieldDisplayOptions,
): string {
  if (value === null || value === undefined) return 'empty'

  if (field === 'date_in_stock_ts') {
    const formatted = formatDealerDateOnly(value, options?.dealerTimeZone)
    return formatted || 'empty'
  }

  if (field === 'certified_by_dealer' || field === 'certified_by_manufacturer') {
    return isTruthy(value) ? 'Certified' : 'Not certified'
  }

  if (isVehicleHistoryBooleanField(field) || isModelEField(field)) {
    return formatBooleanDisplayValue(value)
  }

  if (field === 'condition') {
    const token = normalizeConditionToken(value)
    if (token) return token
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed || 'empty'
  }
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function isTruthy(value: unknown): boolean {
  if (value === true) return true
  if (value === false || value == null) return false
  if (typeof value === 'number') return value !== 0
  const normalized = String(value).trim().toLowerCase()
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'y'
}

function normalizeConditionToken(value: unknown): string {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const upper = raw.toUpperCase()
  if (upper === 'N' || upper === 'NEW' || raw.toLowerCase() === 'new') return 'New'
  if (upper === 'U' || upper === 'USED' || upper === 'C' || upper === 'CPO' || raw.toLowerCase() === 'used') {
    return 'Used'
  }
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
}
